import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, setUser, setToken } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";

/**
 * ResetPassword — second step of the password-reset flow. Reads the token
 * from the URL, collects a new password + confirmation, POSTs to backend,
 * then signs the user straight in and routes to /dashboard.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [missingToken, setMissingToken] = useState(!token);

  useEffect(() => {
    if (!token) setMissingToken(true);
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      setToken(data.token);
      setUser(data.user);
      toast.success("Password updated — you're signed in!");
      navigate(data.user?.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset your password. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-1 text-sm text-[#4A6B56] hover:text-[#1B3624] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <Card className="p-7 sm:p-8 rounded-3xl border-[#E2DFD8] bg-white">
          <div className="inline-flex items-center gap-2 bg-[#EBE8E0] text-[#1B3624] px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-wide uppercase">
            <ShieldCheck className="w-3 h-3" /> Set new password
          </div>
          <h1 className="mt-4 text-2xl sm:text-3xl tracking-tight font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }} data-testid="reset-title">
            Pick a strong password
          </h1>
          <p className="mt-2 text-sm text-[#6B6965]">
            Minimum 8 characters. We'll sign you in as soon as you save it.
          </p>

          {missingToken ? (
            <div data-testid="reset-missing-token" className="mt-6 rounded-xl border border-[#D67B54]/40 bg-[#FDF2EB] p-4 text-sm text-[#7A4A2A]">
              This link is missing its reset token. Please{" "}
              <Link to="/forgot-password" className="text-[#1B3624] underline">request a new one</Link>.
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-password" className="text-sm">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    data-testid="reset-password"
                    type={reveal ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 chars, 1 letter + 1 number"
                    className="rounded-xl pr-10"
                  />
                  <button
                    type="button"
                    data-testid="reset-reveal"
                    onClick={() => setReveal(!reveal)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6965] hover:text-[#1B3624]"
                    aria-label={reveal ? "Hide password" : "Show password"}
                  >
                    {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="text-sm">Confirm password</Label>
                <Input
                  id="confirm-password"
                  data-testid="reset-confirm"
                  type={reveal ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat the same password"
                  className="rounded-xl"
                />
              </div>
              <Button
                data-testid="reset-submit"
                type="submit"
                disabled={loading || !password || !confirm}
                className="w-full rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white py-6"
              >
                {loading ? "Saving…" : "Save & sign in"}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </Layout>
  );
}
